import {
  enumInventoryLevelSortOption,
  enumInventoryViewMode,
  enumLocationFilterType,
  InventoryLevelSortOption,
  InventoryViewMode,
  LocationFilterType,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { inventoryLevelsQueryAtom } from '../_graphql/queryInventoryLevels';
import { inventoryZonesByFacilityQueryAtom } from '../_graphql/queryInventoryZones';

import { atomFromQueryString } from '~/shared/state';

export const $searchTerm = atomFromQueryString.string('q');
export const $selectedFacility = atomFromQueryString.string('f');
export const $selectedZone = atomFromQueryString.string('z');
export const $viewMode = atomFromQueryString.string(
  'view',
  enumInventoryViewMode.OurMaterial as string,
);
export const $locationFilter = atomFromQueryString.string(
  'location',
  enumLocationFilterType.AllLocations as string,
);
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);

export type QuantityFilter = 'all' | 'hideZero' | 'negativeOnly';
export const $quantityFilter = atom<QuantityFilter>('all');
export const $currentSorting = atom<InventoryLevelSortOption[]>([
  enumInventoryLevelSortOption.QuantityDesc,
]);

export const $remoteZonesForFacility = inventoryZonesByFacilityQueryAtom(
  (get) => {
    const selectedFacility = get($selectedFacility);
    // Only fetch zones when a specific facility is selected
    if (!selectedFacility || selectedFacility === 'all') {
      return { skip: true, variables: { facilityId: '' } };
    }
    return {
      variables: { facilityId: selectedFacility },
    };
  },
);

export const $remoteInventoryLevels = inventoryLevelsQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const selectedFacility = get($selectedFacility);
  const selectedZone = get($selectedZone);
  const viewMode = get($viewMode) as InventoryViewMode;
  const locationFilter = get($locationFilter) as LocationFilterType;
  const limit = get($limit);
  const offset = get($offset);
  const sorting = get($currentSorting);

  const params: {
    searchTerm?: string;
    facilityIds?: string[];
    zoneIds?: string[];
    viewMode?: InventoryViewMode;
    locationFilterType?: LocationFilterType;
    hideZeroQuantity?: boolean;
    limit: number;
    offset: number;
    sorting: InventoryLevelSortOption[];
  } = {
    limit,
    offset,
    sorting,
    viewMode,
  };

  const quantityFilter = get($quantityFilter);
  if (
    quantityFilter === 'hideZero' ||
    viewMode === enumInventoryViewMode.TollingMaterial
  ) {
    params.hideZeroQuantity = true;
  }

  if (searchTerm) params.searchTerm = searchTerm;

  // Handle facility/location filter logic
  // Priority: specific facility selection > virtual location filter
  if (viewMode === enumInventoryViewMode.OurMaterial) {
    // If a specific facility is selected, filter by that facility
    if (selectedFacility && selectedFacility !== 'all') {
      params.facilityIds = [selectedFacility];
      // If a zone is also selected, filter by that zone
      if (selectedZone && selectedZone !== 'all') {
        params.zoneIds = [selectedZone];
      }
    } else {
      // Otherwise use the virtual location filter (AllLocations, ExternalProcessors, OurFacilities)
      params.locationFilterType = locationFilter;
    }
  } else if (selectedFacility && selectedFacility !== 'all') {
    params.facilityIds = [selectedFacility];
    // If a zone is also selected, filter by that zone
    if (selectedZone && selectedZone !== 'all') {
      params.zoneIds = [selectedZone];
    }
  }

  return {
    variables: params,
  };
});
